import React, { useState, useMemo } from "react";
import {
  Edit,
  Trash2,
  GripVertical,
  ChevronsUpDown,
  Database,
} from "lucide-react";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Widget } from "@/types/dashboard.types";
import { WidgetRenderer } from "@/components/dashboard/widget-renderer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check } from "lucide-react";
import { useGetWidgetData } from "@/hooks";

interface GroupWidgetCardProps {
  widget: Widget;
  onEdit: (widgetId: string) => void;
  onDelete: (widgetId: string) => void;
  isInteractive?: boolean;
  allWidgets?: Widget[];
}

export default function GroupWidgetCard({
  widget,
  onEdit,
  onDelete,
  isInteractive = true,
  allWidgets = [],
}: GroupWidgetCardProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);

  // Get child widgets from the group config
  const childIds = (widget as any).config?.group?.childrenIds || [];
  const childWidgets = useMemo(() => {
    return childIds
      .map((id: string) => allWidgets.find(w => w.id === id))
      .filter(Boolean) as Widget[];
  }, [childIds, allWidgets]);

  // Auto-select first child if none selected
  const activeChildId = selectedChildId || childWidgets[0]?.id || null;
  const activeChild = childWidgets.find(w => w.id === activeChildId);

  // Fetch data for the active child
  const {
    data: childDataR,
    isLoading: childDataLoading,
    error: childDataError,
  } = useGetWidgetData(activeChildId || "");
  const childData = childDataR?.data;

  const handleSelectChild = (childId: string) => {
    setSelectedChildId(childId);
    setOpen(false);
  };

  if (childWidgets.length === 0) {
    return (
      <Card className="flex w-full h-full flex-col transition-shadow duration-200 group hover:shadow-md">
        <CardHeader className="flex-shrink-0 pb-3">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {isInteractive && (
                <div className="drag-handle cursor-grab rounded p-1 active:cursor-grabbing hover:bg-muted">
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <Database className="h-5 w-5 flex-shrink-0 text-primary" />
              <CardTitle className="min-w-0 flex-1 truncate text-base">
                {widget.title || t('processManagement.untitledGroup')}
              </CardTitle>
            </div>
            <div className="flex flex-shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              <Button
                onClick={() => onEdit(widget.id)}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                onClick={() => onDelete(widget.id)}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-grow flex-col pt-0">
          <div className="flex-grow flex items-center justify-center rounded-lg border bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="text-center text-muted-foreground">
              <Database className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">{t('processManagement.noWidgetsInGroup')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex w-full h-full flex-col transition-shadow duration-200 group hover:shadow-md">
      <CardHeader className="flex-shrink-0 pb-3">
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {isInteractive && (
              <div className="drag-handle cursor-grab rounded p-1 active:cursor-grabbing hover:bg-muted">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <Database className="h-5 w-5 flex-shrink-0 text-primary" />
            <CardTitle className="min-w-0 flex-1 truncate text-base">
              {widget.title || t('processManagement.untitledGroup')}
            </CardTitle>
          </div>
          {/* Edit/Delete buttons */}
          {isInteractive && (
            <div className="flex flex-shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              <Button
                onClick={() => onEdit(widget.id)}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                onClick={() => onDelete(widget.id)}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>

      {/* Widget Selector - spans full widget width */}
      <div className="px-6 pb-3">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full h-8 px-2 text-xs"
            >
              {activeChild ? (
                <div className="flex items-center gap-1 min-w-0">
                  <Database className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{activeChild.title}</span>
                </div>
              ) : (
                <span>{t('processManagement.selectWidget')}</span>
              )}
              <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <Command>
              <CommandInput placeholder={t('processManagement.searchWidgets')} />
              <CommandList className="max-h-[200px]">
                <CommandEmpty className="py-6 text-center text-sm text-gray-500">
                  {t('processManagement.noWidgetsFound')}
                </CommandEmpty>
                <CommandGroup>
                  {childWidgets.map((child) => (
                    <CommandItem
                      key={child.id}
                      value={child.id}
                      onSelect={() => handleSelectChild(child.id)}
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                    >
                      <Database className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm truncate">
                          {child.title || t('processManagement.untitledWidget')}
                        </div>
                      </div>
                      <Check
                        className={`ml-2 h-4 w-4 flex-shrink-0 ${
                          activeChildId === child.id ? "opacity-100" : "opacity-0"
                        }`}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <CardContent className="flex flex-grow flex-col pt-0">
        <div className="relative flex-grow rounded-lg border bg-gradient-to-br from-gray-50 to-gray-100">
          {activeChild ? (
            <WidgetRenderer
              widget={activeChild}
              data={childData}
              loading={childDataLoading}
              error={childDataError?.message}
              isPreview={false}
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="text-center text-muted-foreground">
                <Database className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">{t('processManagement.selectWidgetToView')}</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
